import { ZodError } from "zod";
import { backupWrapperSchema } from "../backup.ts";
import { productionRegistry } from "./production.ts";
import { stepsFrom, type MigrationRegistry } from "./registry.ts";
import {
  parseMigrationContext,
  type FileDecodeFailure,
  type InspectResult,
  type MigrateResult,
  type MigrationContext,
  type MigrationIssue,
  type MigrationReport,
  type SnapshotCounts,
  type StorageFileKind,
} from "./types.ts";

const GENERIC_INVALID_FILE = "That file is not a Vocably vocabulary snapshot.";
const GENERIC_INVALID_SOURCE = "Saved vocabulary does not match its declared schema version.";

export function utf8ByteSize(text: string): number {
  return new TextEncoder().encode(text).length;
}

export function issuesFromZod(error: ZodError): MigrationIssue[] {
  return error.issues.map((issue) => ({
    path: issue.path.length ? issue.path.map(String).join(".") : "(root)",
    code: issue.code,
  }));
}

function asRecord(value: unknown): Record<string, unknown> | null {
  if (value === null || typeof value !== "object" || Array.isArray(value)) {
    return null;
  }
  return value as Record<string, unknown>;
}

export function peekSchemaVersion(
  value: unknown,
): { ok: true; version: number } | { ok: false; issues?: MigrationIssue[] } {
  const record = asRecord(value);
  if (!record) {
    return {
      ok: false,
      issues: [{ path: "(root)", code: "invalid_type" }],
    };
  }
  if (!("schemaVersion" in record)) {
    return {
      ok: false,
      issues: [{ path: "schemaVersion", code: "missing" }],
    };
  }
  const version = record.schemaVersion;
  if (typeof version !== "number" || !Number.isInteger(version) || version < 1 || !Number.isFinite(version)) {
    return {
      ok: false,
      issues: [{ path: "schemaVersion", code: "invalid_version" }],
    };
  }
  return { ok: true, version };
}

function snapshotCounts(value: unknown): SnapshotCounts {
  const record = asRecord(value);
  const sizeOf = (key: string): number => {
    const field = record?.[key];
    return Array.isArray(field) ? field.length : 0;
  };
  return {
    words: sizeOf("words"),
    meanings: sizeOf("meanings"),
    relations: sizeOf("relations"),
    exerciseSets: sizeOf("exerciseSets"),
    practiceStates: sizeOf("practiceStates"),
  };
}

function parseJson(raw: string): { ok: true; value: unknown } | { ok: false; failure: FileDecodeFailure } {
  try {
    return { ok: true, value: JSON.parse(raw) as unknown };
  } catch {
    return {
      ok: false,
      failure: { status: "malformed_json", message: "That file is not valid JSON." },
    };
  }
}

export type UnwrappedStorageFile =
  | {
      fileKind: "snapshot";
      snapshot: unknown;
    }
  | {
      fileKind: "backup";
      snapshot: unknown;
      exportedAt: string;
    };

export function unwrapStorageValue(
  value: unknown,
): { ok: true; file: UnwrappedStorageFile } | { ok: false; failure: FileDecodeFailure } {
  const record = asRecord(value);
  if (record && record.format === "vocably-backup") {
    const parsed = backupWrapperSchema.safeParse(value);
    if (!parsed.success) {
      return {
        ok: false,
        failure: {
          status: "invalid_wrapper",
          message: "That backup wrapper is not a Vocably vocabulary file.",
          issues: issuesFromZod(parsed.error),
        },
      };
    }
    return {
      ok: true,
      file: {
        fileKind: "backup",
        snapshot: parsed.data.snapshot,
        exportedAt: parsed.data.exportedAt,
      },
    };
  }
  return { ok: true, file: { fileKind: "snapshot", snapshot: value } };
}

function classifyVersion(
  version: number,
  registry: MigrationRegistry,
): FileDecodeFailure | undefined {
  if (version > registry.currentVersion) {
    return {
      status: "unsupported_future",
      version,
      message: "This vocabulary was saved by a newer Vocably version.",
    };
  }
  if (version < registry.minSupportedVersion) {
    return {
      status: "unsupported_old",
      version,
      message: "This vocabulary uses a format Vocably can no longer open.",
    };
  }
  return undefined;
}

function inspectUnwrapped(
  snapshot: unknown,
  fileKind: StorageFileKind,
  registry: MigrationRegistry,
  backupExportedAt?: string,
): InspectResult {
  const peeked = peekSchemaVersion(snapshot);
  if (!peeked.ok) {
    return {
      status: "invalid_version",
      message: "That vocabulary is missing a valid schema version.",
      issues: peeked.issues,
    };
  }
  const unsupported = classifyVersion(peeked.version, registry);
  if (unsupported) {
    return unsupported;
  }
  const schema = registry.schemas.get(peeked.version);
  if (!schema) {
    return {
      status: "unsupported_old",
      version: peeked.version,
      message: "This vocabulary uses a format Vocably can no longer open.",
    };
  }
  const parsed = schema.safeParse(snapshot);
  if (!parsed.success) {
    return {
      status: "invalid_source",
      sourceVersion: peeked.version,
      message: GENERIC_INVALID_SOURCE,
      issues: issuesFromZod(parsed.error),
    };
  }
  return {
    status: "ok",
    fileKind,
    sourceVersion: peeked.version,
    targetVersion: registry.currentVersion,
    availableStepIds: stepsFrom(registry, peeked.version).map((step) => step.id),
    ...(backupExportedAt ? { backupExportedAt } : {}),
  };
}

export function inspectStorageFile(
  raw: string,
  registry: MigrationRegistry = productionRegistry,
): InspectResult {
  const parsed = parseJson(raw);
  if (!parsed.ok) {
    return parsed.failure;
  }
  const unwrapped = unwrapStorageValue(parsed.value);
  if (!unwrapped.ok) {
    return unwrapped.failure;
  }
  return inspectUnwrapped(
    unwrapped.file.snapshot,
    unwrapped.file.fileKind,
    registry,
    unwrapped.file.fileKind === "backup" ? unwrapped.file.exportedAt : undefined,
  );
}

function outputVersion(value: unknown): number | undefined {
  const peeked = peekSchemaVersion(value);
  return peeked.ok ? peeked.version : undefined;
}

function bumpDataRevision(value: unknown): unknown {
  const record = asRecord(value);
  if (!record || typeof record.dataRevision !== "number" || !Number.isInteger(record.dataRevision)) {
    return value;
  }
  return { ...record, dataRevision: record.dataRevision + 1 };
}

export function migrateStorageFile(
  raw: string,
  context: MigrationContext,
  registry: MigrationRegistry = productionRegistry,
): MigrateResult {
  const parsedContext = parseMigrationContext(context.migratedAt);
  if ("error" in parsedContext) {
    return { status: "invalid_context", message: parsedContext.error };
  }
  const parsed = parseJson(raw);
  if (!parsed.ok) {
    return parsed.failure;
  }
  const unwrapped = unwrapStorageValue(parsed.value);
  if (!unwrapped.ok) {
    return unwrapped.failure;
  }

  const peeked = peekSchemaVersion(unwrapped.file.snapshot);
  if (!peeked.ok) {
    return {
      status: "invalid_version",
      message: "That vocabulary is missing a valid schema version.",
      issues: peeked.issues,
    };
  }
  const unsupported = classifyVersion(peeked.version, registry);
  if (unsupported) {
    return unsupported;
  }
  const sourceSchema = registry.schemas.get(peeked.version);
  if (!sourceSchema) {
    return {
      status: "unsupported_old",
      version: peeked.version,
      message: "This vocabulary uses a format Vocably can no longer open.",
    };
  }
  const sourceParsed = sourceSchema.safeParse(unwrapped.file.snapshot);
  if (!sourceParsed.success) {
    return {
      status: "invalid_source",
      sourceVersion: peeked.version,
      message: GENERIC_INVALID_SOURCE,
      issues: issuesFromZod(sourceParsed.error),
    };
  }

  const steps = stepsFrom(registry, peeked.version);
  let current: unknown = structuredClone(sourceParsed.data);
  for (const step of steps) {
    let next: unknown;
    try {
      next = step.migrate(structuredClone(current), parsedContext);
    } catch {
      return {
        status: "conversion_failed",
        message: `Migration step ${step.id} failed.`,
        stepId: step.id,
        sourceVersion: peeked.version,
      };
    }
    const nextVersion = outputVersion(next);
    if (nextVersion !== step.toVersion) {
      return {
        status: "conversion_failed",
        message: `Migration step ${step.id} produced the wrong schema version.`,
        stepId: step.id,
        sourceVersion: peeked.version,
        issues: [{ path: "schemaVersion", code: "wrong_output_version" }],
      };
    }
    const targetSchema = registry.schemas.get(step.toVersion);
    if (!targetSchema) {
      return {
        status: "conversion_failed",
        message: `Migration step ${step.id} has no target schema.`,
        stepId: step.id,
        sourceVersion: peeked.version,
      };
    }
    const validated = targetSchema.safeParse(next);
    if (!validated.success) {
      return {
        status: "invalid_intermediate",
        message: `Migration step ${step.id} produced invalid data.`,
        stepId: step.id,
        issues: issuesFromZod(validated.error),
      };
    }
    current = validated.data;
  }

  const currentSchema = registry.schemas.get(registry.currentVersion);
  if (!currentSchema) {
    return {
      status: "invalid_target",
      message: GENERIC_INVALID_FILE,
    };
  }
  const noop = steps.length === 0;
  const finalized = noop ? current : bumpDataRevision(current);
  const targetParsed = currentSchema.safeParse(finalized);
  if (!targetParsed.success) {
    return {
      status: "invalid_target",
      message: "Migrated vocabulary is not valid for the current schema.",
      issues: issuesFromZod(targetParsed.error),
    };
  }

  const serializedSnapshot = JSON.stringify(targetParsed.data);
  return {
    status: "ok",
    snapshot: targetParsed.data,
    serializedSnapshot,
    report: {
      sourceVersion: peeked.version,
      targetVersion: registry.currentVersion,
      appliedStepIds: steps.map((step) => step.id),
      stepCount: steps.length,
      sourceByteSize: utf8ByteSize(raw),
      targetByteSize: utf8ByteSize(serializedSnapshot),
      migratedAt: parsedContext.migratedAt,
      noop,
      fileKind: unwrapped.file.fileKind,
      ...(unwrapped.file.fileKind === "backup" ? { backupExportedAt: unwrapped.file.exportedAt } : {}),
      counts: snapshotCounts(targetParsed.data),
    },
  };
}

export function serializeMigratedFile(snapshot: unknown, report: MigrationReport): string {
  const body =
    report.fileKind === "backup"
      ? {
          format: "vocably-backup",
          formatVersion: 1,
          exportedAt: report.backupExportedAt ?? report.migratedAt,
          snapshot,
        }
      : snapshot;
  return `${JSON.stringify(body, null, 2)}\n`;
}

export function inspectSnapshot(raw: string, registry?: MigrationRegistry): InspectResult {
  return inspectStorageFile(raw, registry);
}

export function migrateSnapshot(
  raw: string,
  context: MigrationContext,
  registry?: MigrationRegistry,
): MigrateResult {
  return migrateStorageFile(raw, context, registry);
}

export function prepareBackup(
  raw: string,
  context: MigrationContext,
  registry?: MigrationRegistry,
): MigrateResult {
  return migrateStorageFile(raw, context, registry);
}
